import React, { useEffect, useRef, useState } from "react";
import { Box, Flex, Image, Link, Text, useMediaQuery } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import SEO from "./SEO";
import Hero from "./Hero";
import FAQ from "./FAQ";
import Contact from "./Contact";
import SocialMedia from "./SocialMedia";
import KeywordMarquee from "./KeywordMarquee";
import HomeStoryIntro from "./HomeStoryIntro";
import HomeV2Sections from "./HomeV2Sections";
import HomeLatestNews from "./HomeLatestNews";
import Footer from "./Footer";
import QRCodeImage from "../images/mascot-QR/QR.png";
import MascotImageEn from "../images/mascot-QR/mascot_with_text_en.png";
import MascotImageZh from "../images/mascot-QR/mascot_with_text_zh_hk.png";
import MascotImageZhCn from "../images/mascot-QR/mascot_with_text_zh_cn.png";
import { normalizeSiteLanguage } from "../i18n/language";

const FLOATING_DOWNLOAD_COLLAPSED_POSITION = "-243px";

const Home: React.FC = () => {
  const { t, i18n } = useTranslation();
  const resolvedLang = normalizeSiteLanguage(i18n.resolvedLanguage || i18n.language);
  const mascotImage =
    resolvedLang === "zh-CN"
      ? MascotImageZhCn
      : resolvedLang === "zh"
        ? MascotImageZh
        : MascotImageEn;
  const [isFloatingDownloadOpen, setIsFloatingDownloadOpen] = useState(false);
  const [shouldHideFloatingDownload] = useMediaQuery("(max-width: 1024px)");
  const lastScrollY = useRef(0);
  const downloadUrl =
    process.env.REACT_APP_U_LINK ||
    "https://deepsoulai.onelink.me/bf43/downloadAppDC";

  useEffect(() => {
    if (shouldHideFloatingDownload) return;

    const handleScroll = () => {
      const heroSection = document.getElementById("hero");
      const storySection = document.getElementById("the-beginning");
      const currentScrollY = window.scrollY;

      if (
        heroSection?.getBoundingClientRect().bottom &&
        heroSection.getBoundingClientRect().bottom > 0
      ) {
        setIsFloatingDownloadOpen(false);
        lastScrollY.current = currentScrollY;
        return;
      }

      if (storySection) {
        const { top } = storySection.getBoundingClientRect();

        if (currentScrollY < lastScrollY.current) {
          setIsFloatingDownloadOpen(true);
        } else if (top < 0) {
          setIsFloatingDownloadOpen(false);
        } else {
          setIsFloatingDownloadOpen(true);
        }
      }

      lastScrollY.current = currentScrollY;
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [shouldHideFloatingDownload]);

  return (
    <Box px={0}>
      <SEO
        title={t("seo.pages.home.title")}
        description={t("seo.pages.home.description")}
        canonical="https://deepsoulai.com/"
      />
      <Box id="hero">
        <Hero />
      </Box>
      <KeywordMarquee />
      <HomeStoryIntro />
      <HomeV2Sections />
      <HomeLatestNews />
      <Box maxW="1200px" mx="auto">
        <FAQ />
        <Contact />
        <SocialMedia />

        {!shouldHideFloatingDownload && (
          <Box
            data-testid="floating-download-widget"
            position="fixed"
            right={
              isFloatingDownloadOpen
                ? "0px"
                : FLOATING_DOWNLOAD_COLLAPSED_POSITION
            }
            top="80%"
            transform="translateY(-50%)"
            width="300px"
            height="auto"
            bg="transparent"
            transition="right 0.5s"
            onMouseEnter={() => setIsFloatingDownloadOpen(true)}
            onMouseLeave={() => setIsFloatingDownloadOpen(false)}
            zIndex={1000}
          >
            <Flex alignItems="center">
              <Box
                as="button"
                type="button"
                data-testid="floating-download-toggle"
                width="120px"
                flexShrink={0}
                border={0}
                bg="transparent"
                cursor="pointer"
                aria-label={t("home.qr.button")}
                onClick={() => setIsFloatingDownloadOpen((isOpen) => !isOpen)}
                _focusVisible={{
                  outline: "3px solid",
                  outlineColor: "primary.300",
                  outlineOffset: "2px",
                  borderRadius: "md",
                }}
              >
                <Image
                  data-testid="floating-download-mascot"
                  src={mascotImage}
                  alt=""
                  width="100%"
                  height="auto"
                  objectFit="contain"
                />
              </Box>

              <Link
                href={downloadUrl}
                isExternal
                display="block"
                width="180px"
                flexShrink={0}
                textDecoration="none"
                aria-label={t("home.qr.openLinkLabel")}
                _hover={{ textDecoration: "none" }}
                _focusVisible={{
                  outline: "3px solid",
                  outlineColor: "primary.300",
                  outlineOffset: "2px",
                  borderRadius: "md",
                }}
              >
                <Box
                  bg="rgba(255, 255, 255, 0.7)"
                  boxShadow="md"
                  borderRadius="md"
                  padding="20px"
                  width="180px"
                  border="2px solid #d7a6ffff"
                >
                  <Image
                    src={QRCodeImage}
                    alt={t("home.qr.imageAlt")}
                    width="100%"
                    height="auto"
                    objectFit="contain"
                  />
                  <Text mt={2} textAlign="center" fontWeight="bold">
                    {t("home.qr.title")}
                  </Text>
                </Box>
              </Link>
            </Flex>
          </Box>
        )}
      </Box>
      <Footer />
    </Box>
  );
};

export default Home;
